/**
 *
 * DeleteAccount
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  Button,
  FormGroup,
  ControlLabel,
  FormControl,
  Checkbox,
} from 'react-bootstrap';
import { API2_URL } from 'config/constants';
import { request2 } from 'utils/request';
import { red700 } from 'styles/colors';
import Spinner from 'components/Spinner';
import messages from './messages';

function DeleteAccount({ user, userData, userEmail, onDeleteSuccess }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [confirmPublic, setConfirmPublic] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  function handleDeleteAccount() {
    setDeleteError(null);
    setDeleting(true);

    const formData = new URLSearchParams();
    formData.append('email', emailInput);
    if (userData?.nPublic > 0) {
      formData.append('confirmPublic', confirmPublic ? 'true' : 'false');
    }

    request2({
      method: 'DELETE',
      url: `${API2_URL}/user/${user.api.userId}?${formData.toString()}`,
      user,
    })
      .then(() => {
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      })
      .catch(err => {
        setDeleting(false);
        setDeleteError(err.message || 'Failed to delete account');
      });
  }

  function isValidForm() {
    if (emailInput !== userEmail) return false;
    if (userData?.nPublic > 0 && !confirmPublic) return false;
    return true;
  }

  function handleCancel() {
    setShowDeleteConfirm(false);
    setEmailInput('');
    setConfirmPublic(false);
    setDeleteError(null);
  }

  if (!user.api) {
    return null;
  }

  return (
    <>
      {/* eslint-disable indent, prettier/prettier */}
      {!showDeleteConfirm && (
        <div className="mt-4">
          <button
            type="button"
            style={{
              color: red700,
              fontSize: '0.9em',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
            onClick={() => setShowDeleteConfirm(true)}
          >
            <FormattedMessage {...messages.deleteAccount} />
          </button>
        </div>
      )}
      {/* eslint-enable indent, prettier/prettier */}
      {/* eslint-disable indent, prettier/prettier, react/jsx-no-bind */}
      {showDeleteConfirm && (
        <div className="mt-4 p-3" style={{ border: '2px solid #d9534f' }}>
          <h4 className="text-danger">
            <FormattedMessage {...messages.deleteAccountTitle} />
          </h4>
          <Alert bsStyle="danger">
            <strong>
              <FormattedMessage {...messages.deleteWarning} />
            </strong>
            <br />
            <FormattedMessage {...messages.deleteDataNote} />
          </Alert>
          {userData?.nPublic > 0 && (
            <Alert bsStyle="warning">
              <FormattedMessage
                {...messages.publicRoutesWarning}
                values={{ count: userData.nPublic }}
              />
            </Alert>
          )}
          <FormGroup>
            <ControlLabel>
              <FormattedMessage {...messages.confirmEmailLabel} />
            </ControlLabel>
            <FormControl
              type="email"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder={userEmail}
            />
          </FormGroup>
          {userData?.nPublic > 0 && (
            <FormGroup>
              <Checkbox
                checked={confirmPublic}
                onChange={e => setConfirmPublic(e.target.checked)}
              >
                <FormattedMessage
                  {...messages.confirmPublicRoutes}
                  values={{ count: userData.nPublic }}
                />
              </Checkbox>
            </FormGroup>
          )}
          {deleteError && <Alert bsStyle="danger">{deleteError}</Alert>}
          <div className="text-right mt-3">
            <Button
              bsStyle="default"
              onClick={handleCancel}
              disabled={deleting}
              className="mr-2"
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button
              bsStyle="danger"
              onClick={handleDeleteAccount}
              disabled={!isValidForm() || deleting}
            >
              {deleting ? (
                <Spinner />
              ) : (
                <FormattedMessage {...messages.deleteAccount} />
              )}
            </Button>
          </div>
        </div>
      )}
      {/* eslint-enable indent, prettier/prettier, react/jsx-no-bind */}
    </>
  );
}

DeleteAccount.propTypes = {
  user: PropTypes.object.isRequired,
  userData: PropTypes.object,
  userEmail: PropTypes.string.isRequired,
  onDeleteSuccess: PropTypes.func,
};

export default DeleteAccount;
